(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════
     HURACAN — FORMLESS ENERGY ENTITY
     A plasma creature — no hard shape, pure glowing energy.
     Perches ghosted under the nav CTA, barely visible.
     Any interaction: charges, then glides across the page.
  ═══════════════════════════════════════════════════════════ */

  /* ── Button spotlight CSS ───────────────────────────────── */
  const style = document.createElement('style');
  style.textContent = `
    .btn-ghost {
      --mx:50%;--my:50%;position:relative;overflow:hidden;
      transition:border-color .25s,box-shadow .25s,background .25s;
    }
    .btn-ghost::after {
      content:'';position:absolute;inset:0;border-radius:inherit;
      background:radial-gradient(circle 90px at var(--mx) var(--my),
        rgba(0,212,255,.26) 0%,transparent 70%);
      opacity:0;transition:opacity .18s ease;pointer-events:none;
    }
    .btn-ghost:hover::after{opacity:1;}
    .btn-ghost:hover{
      border-color:rgba(0,212,255,.5);
      box-shadow:0 0 16px 2px rgba(0,212,255,.15);
    }
    .nav-cta::after{
      background:radial-gradient(circle 80px at var(--mx) var(--my),
        rgba(255,255,255,.55) 0%,transparent 70%);
    }
    .nav-cta:hover{
      border-color:rgba(255,255,255,.9)!important;
      box-shadow:0 0 26px 8px rgba(255,255,255,.9),
                 0 0 64px 18px rgba(255,255,255,.35)!important;
    }
  `;
  document.head.appendChild(style);

  function attachSpotlight(el) {
    if (el.dataset.sp) return;
    el.dataset.sp = '1';
    el.addEventListener('mousemove', e => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', (e.clientX - r.left) + 'px');
      el.style.setProperty('--my', (e.clientY - r.top)  + 'px');
    });
  }
  function initSpotlights() {
    document.querySelectorAll(
      '.btn-ghost, .about-stat, .about-why-item, .about-cred-item, ' +
      '.netcc-item, .netcc-concern-step, .faq-item, ' +
      '.feature-cell, .cert-item, .review-card, .commercial-item, .spec-row'
    ).forEach(attachSpotlight);
  }
  initSpotlights();
  new MutationObserver(initSpotlights).observe(document.body, { childList: true, subtree: true });

  /* ── Canvas ─────────────────────────────────────────────── */
  const canvas = document.createElement('canvas');
  canvas.id = 'creature-canvas';
  canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9997;';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  let W, H;
  function resize() { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; }
  window.addEventListener('resize', resize, { passive: true });
  resize();

  /* ── Bezier point sampler ───────────────────────────────── */
  function bezierPts(p0, p1, p2, p3, n) {
    const pts = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n, m = 1 - t;
      pts.push([
        m*m*m*p0[0] + 3*m*m*t*p1[0] + 3*m*t*t*p2[0] + t*t*t*p3[0],
        m*m*m*p0[1] + 3*m*m*t*p1[1] + 3*m*t*t*p2[1] + t*t*t*p3[1],
      ]);
    }
    return pts;
  }

  /* draw tapered glowing arc: thick at i=0, fades to nothing */
  function drawArc(pts, maxW, rgba, alpha) {
    const n = pts.length - 1;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.shadowBlur  = 14;
    ctx.shadowColor = '#00d4ff';
    for (let i = 0; i < n; i++) {
      const t = 1 - i / n;
      ctx.beginPath();
      ctx.moveTo(pts[i][0],     pts[i][1]);
      ctx.lineTo(pts[i+1][0],   pts[i+1][1]);
      ctx.strokeStyle = rgba.replace('§', (t * alpha).toFixed(2));
      ctx.lineWidth   = t * maxW + 0.3;
      ctx.stroke();
    }
    ctx.restore();
  }

  /* ══════════════════════════════════════════════════════════
     DRAW FORMLESS ENERGY ENTITY
     No filled polygon shapes — only radial gradients and
     tapered glowing arcs. Pure plasma / electricity.
  ══════════════════════════════════════════════════════════ */
  function drawEntity(x, y, angle, alpha, glowT) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.globalAlpha = alpha;

    const pulse = 0.80 + Math.sin(glowT * 3.2) * 0.20;

    /* outer diffuse halo */
    const halo = ctx.createRadialGradient(0,0,0, 0,0, 58);
    halo.addColorStop(0,   `rgba(0,200,255,${(0.14*pulse).toFixed(2)})`);
    halo.addColorStop(0.5,  'rgba(0,160,220,0.04)');
    halo.addColorStop(1,    'rgba(0,100,180,0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(0, 0, 58, 0, Math.PI * 2);
    ctx.fill();

    /* ── wing tendrils — swept-back energy arcs ─────────── */
    /* upper arc: sweeps up and backward */
    const wU = bezierPts([ 4, -1], [-2,-20], [-18,-28], [-34,-12], 20);
    /* lower arc: mirror */
    const wD = bezierPts([ 4,  1], [-2, 20], [-18, 28], [-34, 12], 20);
    /* inner secondary arc — closer to body */
    const iU = bezierPts([ 2, -1], [-1,-10], [ -9,-14], [-20, -6], 12);
    const iD = bezierPts([ 2,  1], [-1, 10], [ -9, 14], [-20,  6], 12);

    ctx.shadowBlur  = 18;
    ctx.shadowColor = '#00d4ff';

    /* glow layer */
    drawArc(wU, 8,   'rgba(0,195,255,§)',   0.22 * pulse);
    drawArc(wD, 8,   'rgba(0,195,255,§)',   0.22 * pulse);
    /* bright core */
    drawArc(wU, 2,   'rgba(130,238,255,§)', 0.88);
    drawArc(wD, 2,   'rgba(130,238,255,§)', 0.88);
    /* inner wisps */
    drawArc(iU, 1.2, 'rgba(200,250,255,§)', 0.55);
    drawArc(iD, 1.2, 'rgba(200,250,255,§)', 0.55);

    /* ── nucleus / core glow ────────────────────────────── */
    const r = 10 + pulse * 3;
    const core = ctx.createRadialGradient(2,0,0, 2,0, r * 1.6);
    core.addColorStop(0,    `rgba(255,255,255,${(0.95*pulse).toFixed(2)})`);
    core.addColorStop(0.2,  `rgba(0,235,255,${(0.85*pulse).toFixed(2)})`);
    core.addColorStop(0.55, 'rgba(0,175,255,0.28)');
    core.addColorStop(1,    'rgba(0,110,200,0)');
    ctx.fillStyle = core;
    ctx.shadowBlur  = 24;
    ctx.shadowColor = '#00d4ff';
    ctx.beginPath();
    ctx.arc(2, 0, r * 1.6, 0, Math.PI * 2);
    ctx.fill();

    /* tiny bright leading point — just a hint of direction */
    ctx.shadowBlur  = 12;
    ctx.shadowColor = '#ffffff';
    ctx.fillStyle   = `rgba(255,255,255,${(0.75*pulse).toFixed(2)})`;
    ctx.beginPath();
    ctx.arc(12, 0, 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  /* ══════════════════════════════════════════════════════════
     ENERGY TAIL
  ══════════════════════════════════════════════════════════ */
  function drawTail(history, alpha) {
    const n = history.length;
    if (n < 4) return;
    ctx.save();
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';

    /* outer glow */
    ctx.shadowBlur  = 24;
    ctx.shadowColor = '#00d4ff';
    for (let i = 0; i < n - 1; i++) {
      const t = 1 - i / n;
      ctx.beginPath();
      ctx.moveTo(history[i].x,   history[i].y);
      ctx.lineTo(history[i+1].x, history[i+1].y);
      ctx.strokeStyle = `rgba(0,210,255,${(t * alpha * 0.24).toFixed(2)})`;
      ctx.lineWidth   = t * 16 + 1;
      ctx.stroke();
    }
    /* bright core */
    ctx.shadowBlur = 8;
    for (let i = 0; i < n - 1; i++) {
      const t = 1 - i / n;
      ctx.beginPath();
      ctx.moveTo(history[i].x,   history[i].y);
      ctx.lineTo(history[i+1].x, history[i+1].y);
      ctx.strokeStyle = `rgba(170,245,255,${(t * alpha * 0.88).toFixed(2)})`;
      ctx.lineWidth   = t * 3 + 0.3;
      ctx.stroke();
    }
    ctx.restore();
  }

  /* ══════════════════════════════════════════════════════════
     STATE MACHINE  (velocity-based physics — no heading drift)
  ══════════════════════════════════════════════════════════ */
  const S = { RESTING: 0, STIRRING: 1, FLOWING: 2, RETURNING: 3 };

  const bird = {
    x: 0, y: 0,
    px: 0, py: 0,
    vx: 0, vy: 0,
    heading: 0,        // derived from atan2(vy,vx) — used for drawing only
    alpha: 0.10,
    state: S.RESTING,
    history: [],
    driftT: 0,
    stirTicks: 0,
    restTimer: 0,
    REST_DELAY: 360,
    glowT: 0,
  };

  function findPerch() {
    const cta = document.querySelector('.nav-cta');
    if (cta) {
      const r = cta.getBoundingClientRect();
      bird.px = r.left + r.width  / 2;
      bird.py = r.bottom + 28;
    } else {
      bird.px = W * 0.87;
      bird.py = 88;
    }
  }

  function initPos() { findPerch(); bird.x = bird.px; bird.y = bird.py; }

  window.addEventListener('resize', () => {
    resize(); findPerch();
    if (bird.state === S.RESTING) { bird.x = bird.px; bird.y = bird.py; }
  });

  /* ── Interaction ────────────────────────────────────────── */
  let lastInteract = 0;
  function onInteract() {
    const now = Date.now();
    if (now - lastInteract < 120) return;
    lastInteract = now;
    if (bird.state === S.RESTING || bird.state === S.RETURNING) {
      bird.state = S.STIRRING;
      bird.stirTicks = 0;
    }
    bird.restTimer = bird.REST_DELAY;
  }

  let lastMouse = 0;
  document.addEventListener('mousemove', () => {
    const now = Date.now();
    if (now - lastMouse > 250) { lastMouse = now; onInteract(); }
  });
  window.addEventListener('scroll',      () => onInteract(), { passive: true });
  document.addEventListener('click',     () => onInteract());
  document.addEventListener('mouseover', e => {
    if (e.target.closest('.btn-ghost, .mob-link, button')) onInteract();
  });

  /* ── Velocity wall containment ──────────────────────────── */
  function containVelocity() {
    const PAD = 160, M = 50;

    /* proportional push — velocity directly adjusted */
    if (bird.x < PAD)     bird.vx += (PAD - bird.x)     / PAD * 0.55;
    if (bird.x > W - PAD) bird.vx -= (bird.x - W + PAD) / PAD * 0.55;
    if (bird.y < PAD)     bird.vy += (PAD - bird.y)     / PAD * 0.55;
    if (bird.y > H - PAD) bird.vy -= (bird.y - H + PAD) / PAD * 0.55;

    /* hard bounce */
    if (bird.x < M)     { bird.x = M;     bird.vx =  Math.abs(bird.vx) * 0.7; }
    if (bird.x > W - M) { bird.x = W - M; bird.vx = -Math.abs(bird.vx) * 0.7; }
    if (bird.y < M)     { bird.y = M;     bird.vy =  Math.abs(bird.vy) * 0.7; }
    if (bird.y > H - M) { bird.y = H - M; bird.vy = -Math.abs(bird.vy) * 0.7; }
  }

  function clampSpeed(min, max) {
    const spd = Math.hypot(bird.vx, bird.vy);
    if (spd > max && spd > 0) { bird.vx = bird.vx/spd*max; bird.vy = bird.vy/spd*max; }
    if (spd < min && spd > 0) { bird.vx = bird.vx/spd*min; bird.vy = bird.vy/spd*min; }
  }

  /* ── Update ─────────────────────────────────────────────── */
  function update() {
    bird.glowT += 0.05;

    if (bird.state === S.RESTING) {
      bird.x = bird.px; bird.y = bird.py;
      bird.vx = 0; bird.vy = 0;
      bird.history = []; bird.heading = 0;
      bird.alpha  += (0.10 - bird.alpha) * 0.04;

    } else if (bird.state === S.STIRRING) {
      bird.x = bird.px; bird.y = bird.py;
      bird.stirTicks++;
      bird.alpha += (0.72 - bird.alpha) * 0.08;
      if (bird.stirTicks >= 44) {
        bird.state  = S.FLOWING;
        bird.driftT = 0;
        bird.history = [];
        /* launch diagonally inward — away from top-right corner */
        bird.vx = -2.0;
        bird.vy =  1.8;
      }

    } else if (bird.state === S.FLOWING) {
      /* smooth wander: two incommensurate sine waves perturb velocity */
      bird.driftT += 0.005;
      bird.vx += Math.sin(bird.driftT * 1.7) * 0.055
               + Math.cos(bird.driftT * 0.9) * 0.030;
      bird.vy += Math.cos(bird.driftT * 1.3) * 0.048
               + Math.sin(bird.driftT * 0.7) * 0.028;

      containVelocity();
      clampSpeed(1.8, 3.0);

      bird.x += bird.vx;
      bird.y += bird.vy;
      bird.heading = Math.atan2(bird.vy, bird.vx);
      bird.alpha  += (0.82 - bird.alpha) * 0.05;

      bird.history.unshift({ x: bird.x, y: bird.y });
      if (bird.history.length > 70) bird.history.pop();

      bird.restTimer--;
      if (bird.restTimer <= 0) bird.state = S.RETURNING;

    } else if (bird.state === S.RETURNING) {
      const dx   = bird.px - bird.x;
      const dy   = bird.py - bird.y;
      const dist = Math.hypot(dx, dy);

      if (dist > 1) {
        bird.vx += (dx / dist) * 0.28;
        bird.vy += (dy / dist) * 0.28;
      }

      clampSpeed(0, 3.8);

      bird.x += bird.vx;
      bird.y += bird.vy;
      bird.heading = Math.atan2(bird.vy, bird.vx);
      bird.alpha  += (0.11 - bird.alpha) * 0.03;

      bird.history.unshift({ x: bird.x, y: bird.y });
      if (bird.history.length > 32) bird.history.pop();

      if (dist < 22) {
        bird.x = bird.px; bird.y = bird.py;
        bird.vx = 0; bird.vy = 0;
        bird.state = S.RESTING; bird.history = []; bird.heading = 0;
      }

      if (Date.now() - lastInteract < 160) {
        bird.restTimer = bird.REST_DELAY;
        bird.state     = S.FLOWING;
      }
    }
  }

  /* ── Render ─────────────────────────────────────────────── */
  let lastT = 0;
  function draw(ts) {
    const dt = Math.min((ts - lastT) / 1000, 0.05);
    lastT = ts;

    ctx.clearRect(0, 0, W, H);
    update();

    /* charge-up pulse at perch */
    if (bird.state === S.STIRRING) {
      const p  = 0.5 + Math.sin(bird.glowT * 5) * 0.5;
      const r  = 26 + p * 22;
      const g  = ctx.createRadialGradient(bird.x, bird.y, 0, bird.x, bird.y, r);
      g.addColorStop(0, `rgba(0,212,255,${(p * 0.35).toFixed(2)})`);
      g.addColorStop(1,  'rgba(0,212,255,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(bird.x, bird.y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    drawTail(bird.history, bird.alpha);
    drawEntity(bird.x, bird.y, bird.heading, bird.alpha, bird.glowT);
    drawSparks(dt);

    requestAnimationFrame(draw);
  }

  requestAnimationFrame(() => { initPos(); requestAnimationFrame(draw); });

  /* ══════════════════════════════════════════════════════════
     LOGO LIGHTNING — blue electric arcs from the nav logo
  ══════════════════════════════════════════════════════════ */
  function boltPts(x1, y1, x2, y2, rough, depth) {
    if (depth === 0) return [[x2, y2]];
    const mx = (x1 + x2) / 2 + (Math.random() - 0.5) * rough;
    const my = (y1 + y2) / 2 + (Math.random() - 0.5) * rough;
    return [
      ...boltPts(x1, y1, mx, my, rough * 0.55, depth - 1),
      ...boltPts(mx, my, x2, y2, rough * 0.55, depth - 1),
    ];
  }

  const sparks = [];
  let nextSpark = Date.now() + 2500;

  function fireSparks() {
    const logo = document.querySelector('.nav-logo');
    if (!logo) return;
    const r  = logo.getBoundingClientRect();
    const cx = r.left + r.width  / 2;
    const cy = r.top  + r.height / 2;
    const n  = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < n; i++) {
      const angle = Math.random() * Math.PI * 2;
      const len   = 38 + Math.random() * 55;
      sparks.push({
        pts:  [[cx, cy], ...boltPts(cx, cy,
                cx + Math.cos(angle) * len,
                cy + Math.sin(angle) * len, 18 + Math.random() * 14, 4)],
        life: 0.14 + Math.random() * 0.18,
        age:  0,
      });
    }
    nextSpark = Date.now() + 3500 + Math.random() * 5500;
  }

  function drawSparks(dt) {
    if (Date.now() >= nextSpark && sparks.length === 0) fireSparks();
    for (let i = sparks.length - 1; i >= 0; i--) {
      const s = sparks[i];
      s.age += dt;
      if (s.age >= s.life) { sparks.splice(i, 1); continue; }
      const t = 1 - s.age / s.life;
      ctx.save();
      ctx.lineCap = 'round';
      ctx.shadowBlur = 16; ctx.shadowColor = '#00d4ff';
      ctx.beginPath();
      ctx.moveTo(s.pts[0][0], s.pts[0][1]);
      for (let j = 1; j < s.pts.length; j++) ctx.lineTo(s.pts[j][0], s.pts[j][1]);
      ctx.strokeStyle = `rgba(0,200,255,${(t * 0.55).toFixed(2)})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(s.pts[0][0], s.pts[0][1]);
      for (let j = 1; j < s.pts.length; j++) ctx.lineTo(s.pts[j][0], s.pts[j][1]);
      ctx.strokeStyle = `rgba(210,248,255,${(t * 0.95).toFixed(2)})`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    }
  }

  /* hover fires a burst */
  document.addEventListener('mouseover', e => {
    if (e.target.closest('.nav-logo')) fireSparks();
  });

})();
